<script setup lang="ts">
import { computed, ref } from 'vue'
import { useAppConfig } from '#imports'

const appConfig = useAppConfig()
const shellConfig = computed(() => appConfig.shellConfig || {})
const themeConfig = computed(() => appConfig.theme || {})
const mobileMenuOpen = ref(false)

// Computed theme colors
const themeColors = computed(() => ({
  background: themeConfig.value?.colors?.background || '#ffffff',
  border: themeConfig.value?.colors?.border || '#e5e7eb',
  surface: themeConfig.value?.colors?.surface || '#f9fafb',
  textPrimary: themeConfig.value?.colors?.text?.primary || '#1f2937',
  textSecondary: themeConfig.value?.colors?.text?.secondary || '#6b7280',
  accent: themeConfig.value?.colors?.product?.qx?.accent || '#9ca3af',
}))

const validNavItems = computed(() => {
  const items = shellConfig.value?.navigationMenu || []
  return items.filter((item) => item.route?.startsWith('/'))
})

const handleNavClick = () => {
  mobileMenuOpen.value = false
}
</script>

<template>
  <div
    class="min-h-screen flex flex-col"
    :style="{ backgroundColor: themeColors.background }"
  >
    <header
      class="border-b"
      :style="{ borderBottomColor: themeColors.border }"
    >
      <nav class="flex items-center justify-between px-4 py-4 max-w-7xl mx-auto">
        <div class="flex-shrink-0">
          <img
            v-if="shellConfig?.logo?.src"
            :src="shellConfig.logo.src"
            :alt="shellConfig.logo.alt || 'QxAIOS logo'"
            :width="shellConfig.logo.width || 96"
            :height="shellConfig.logo.height || 32"
            class="h-8 w-auto"
          >
          <div
            v-else
            class="w-10 h-10 rounded flex items-center justify-center text-xs transition-colors"
            :style="{
              backgroundColor: themeColors.accent,
              color: themeColors.textPrimary,
            }"
          >
            Logo
          </div>
        </div>

        <div class="hidden md:flex gap-8">
          <NuxtLink
            v-for="item in validNavItems"
            :key="item.route"
            :to="item.route"
            :aria-label="item.ariaLabel"
            class="text-sm font-medium transition-colors"
            :style="{ color: themeColors.textPrimary }"
            @click="handleNavClick()"
          >
            <span
              v-if="item.icon"
              :class="item.icon"
              class="inline-block mr-2"
            />
            {{ item.label }}
          </NuxtLink>
        </div>

        <UButton
          class="md:hidden"
          variant="ghost"
          :icon="mobileMenuOpen ? 'i-heroicons-x-mark' : 'i-heroicons-bars-3'"
          :aria-label="mobileMenuOpen ? 'Close menu' : 'Open menu'"
          :aria-expanded="mobileMenuOpen"
          data-testid="hamburger-toggle"
          @click="mobileMenuOpen = !mobileMenuOpen"
        />
      </nav>

      <nav
        v-if="mobileMenuOpen"
        class="md:hidden border-t"
        :style="{ 
          backgroundColor: themeColors.surface,
          borderTopColor: themeColors.border,
        }"
        data-testid="mobile-menu"
      >
        <div class="px-4 py-3 space-y-2">
          <NuxtLink
            v-for="item in validNavItems"
            :key="item.route"
            :to="item.route"
            class="block px-3 py-2 text-sm font-medium rounded transition-colors"
            :style="{ color: themeColors.textPrimary }"
            :aria-label="item.ariaLabel"
            data-testid="mobile-nav-item"
            @click="handleNavClick()"
          >
            <span
              v-if="item.icon"
              :class="item.icon"
              class="inline-block mr-2"
            />
            {{ item.label }}
          </NuxtLink>
        </div>
      </nav>
    </header>

    <main class="flex-grow w-full px-0 py-0">
      <slot />
    </main>

    <footer
      v-if="shellConfig?.footer"
      class="border-t mt-12"
      :style="{ 
        backgroundColor: themeColors.surface,
        borderTopColor: themeColors.border,
      }"
    >
      <div class="max-w-7xl mx-auto px-4 py-8">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          <div>
            <p
              class="text-sm"
              :style="{ color: themeColors.textSecondary }"
            >
              © {{ shellConfig.footer.copyrightYear }}
              <span class="font-medium">{{ shellConfig.footer.companyName }}</span>
            </p>
          </div>

          <nav
            v-if="shellConfig.footer.links?.length"
            class="md:col-span-2"
          >
            <ul class="flex flex-col md:flex-row gap-4 md:gap-8">
              <li
                v-for="link in shellConfig.footer.links"
                :key="link.url"
              >
                <a
                  :href="link.url"
                  :target="link.external ? '_blank' : undefined"
                  :rel="link.external ? 'noopener noreferrer' : undefined"
                  class="text-sm transition-colors"
                  :style="{ color: themeColors.textSecondary }"
                  data-testid="footer-link"
                >
                  {{ link.label }}
                </a>
              </li>
            </ul>
          </nav>
        </div>

        <div
          class="border-t pt-4"
          :style="{ borderTopColor: themeColors.border }"
        >
          <p
            class="text-xs text-center"
            :style="{ color: themeColors.textSecondary }"
          >
            Built with <span class="font-semibold">Nuxt 4</span> and
            <span class="font-semibold">Nuxt UI</span>
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<style scoped>
:global(html) {
  height: 100%;
}

:global(body) {
  min-height: 100%;
}
</style>
